<template>
  <div v-if="showRTBMetricsGraph">
    <div class="pane-header">
      <span class="icon"><icon-colored-bidding-stats /></span>
      <span class="text">{{ capitalize($t('messages.biddingActivity')) }}</span>
    </div>
    <div class="row">
      <div class="col-12">
        <div class="pane-content pad-top">
          <div class="row">
            <div class="col-12">
              <div class="time-ranges-container">
                <template v-if="filterStartDate == null">
                  <span class="badge badge-disabled">
                    <icon-clock />
                    <span v-if="entity.hasEnded">{{
                      isDeal ? $t('messages.last24HoursOfTheDeal') : $t('messages.last24HoursOfTheLineItem')
                    }}</span>
                    <span v-else>{{ $t('messages.24HoursFromLastBid') }}</span>
                  </span>
                </template>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <div class="kpis">
                <div class="kpi">
                  <icon-colored-bid-request />
                  <div class="value-and-label">
                    <div class="value">
                      {{ requestsFormatted }}
                    </div>
                    <div class="label">
                      {{ capitalize($t('names.request', { n: 2 })) }}
                    </div>
                  </div>
                </div>
                <div class="kpi">
                  <icon-colored-bid-accepted />
                  <div class="value-and-label">
                    <div class="value">
                      <span
                        v-if="acceptedBidsFormatted != DASH"
                        class="badge badge-success"
                        >{{ acceptedBidsFormatted }}</span
                      >
                      <span v-else>{{ acceptedBidsFormatted }}</span>
                      <span
                        v-if="acceptedBidsFormatted != DASH"
                        class="percent"
                        >({{ acceptedBidsPercent }})</span
                      >
                    </div>
                    <div class="label">
                      {{ capitalize($t('states.accepted')) }}
                    </div>
                  </div>
                </div>
                <div class="kpi">
                  <icon-colored-bid-none />
                  <div class="value-and-label">
                    <div class="value">
                      {{ noBidsFormatted }}
                      <span
                        v-if="noBidsFormatted != DASH"
                        class="percent"
                        >({{ noBidsPercent }})</span
                      >
                    </div>
                    <div class="label">
                      {{ capitalize($t('names.noBid', { n: 2 })) }}
                    </div>
                  </div>
                </div>
                <div class="kpi">
                  <icon-colored-bid-failure />
                  <div class="value-and-label">
                    <div class="value">
                      <span
                        v-if="failuresFormatted != DASH"
                        class="badge badge-danger"
                        >{{ failuresFormatted }}</span
                      >
                      <span v-else>{{ failuresFormatted }}</span>
                      <span
                        v-if="failuresFormatted != DASH"
                        class="percent"
                        >({{ failuresPercent }})</span
                      >
                    </div>
                    <div class="label">
                      {{ capitalize($t('names.failure', { n: 2 })) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row pad-top">
            <div class="col-12">
              <div class="chart-container">
                <chart
                  ref="timelineDataChart"
                  template-ref="timelineDataChart"
                  :name="capitalize($t('messages.biddingActivity'))"
                  :mode="chartMode.COMPACT"
                  :actions="chartActions"
                  :echarts-options="timelineDataChartOptions"
                  :theme="chartTheme.BIDS"
                  @update-chart-type="updateChartType"
                  @update-chart-granularity="updateChartGranularity" />
              </div>
              <div
                class="flex-h"
                style="flex-direction: row-reverse; margin-top: -2.2rem; font-size: 0.9em">
                <span class="text-muted"
                  ><i
                    >({{ capitalize($t('names.timezone', { n: 1 })) }}: UTC{{
                      -new Date().getTimezoneOffset() > 0 ? '+' : ''
                    }}{{ -(new Date().getTimezoneOffset() / 60) }})</i
                  ></span
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { DateTime, Interval } from 'luxon';
  import { capitalize } from 'vue';
  import { mapActions } from 'vuex';

  import Chart from '@/common/components/chart/Chart.vue';
  import ChartDownloadGroupActionsUtils from '@/common/components/chart/chart-download-group-actions';
  import ChartFullscreenActions from '@/common/components/chart/chart-fullscreen-actions';
  import ChartIntervalGroupActionsUtils from '@/common/components/chart/chart-interval-group-actions';
  import ChartTimezoneGroupActionsUtils from '@/common/components/chart/chart-timezone-group-actions';
  import ChartTypeGroupActions from '@/common/components/chart/chart-type-group-actions';
  import CellType from '@/common/enums/cell-type.enum';
  import ChartActionId from '@/common/enums/chart-action-id.enum';
  import ChartMode from '@/common/enums/chart-mode.enum';
  import ChartTheme from '@/common/enums/chart-theme.enum';
  import ChartType from '@/common/enums/chart-type.enum';
  import TimeGranularity from '@/common/enums/time-granularity.enum';
  import {
    IconColoredBidAccepted,
    IconColoredBiddingStats,
    IconColoredBidFailure,
    IconColoredBidNone,
    IconColoredBidRequest,
  } from '@/common/icons/colored';
  import { IconClock } from '@/common/icons/monochrome';
  import { Package } from '@/common/models';
  import TimeseriesAxisX from '@/common/models/chart/TimeseriesAxisX';
  import { DASH } from '@/globals';
  import LocalCache from '@/utils/LocalCache';
  import formatNumber from '@/utils/NumberFormatter';
  import QuickMathUtils from '@/utils/QuickMathUtils';
  import i18n from '@/vueI18n';

  export default {
    name: 'RTBMetrics',
    components: {
      Chart,
      IconClock,
      IconColoredBiddingStats,
      IconColoredBidAccepted,
      IconColoredBidFailure,
      IconColoredBidNone,
      IconColoredBidRequest,
    },
    props: {
      entity: Object,
      filterStartDate: DateTime,
      filterEndDate: DateTime,
    },
    data() {
      return {
        chartActions: [
          ChartTypeGroupActions.getActions(0, true, [ChartType.LINE, ChartType.BAR], ChartType.BAR),
          ChartIntervalGroupActionsUtils.ChartIntervalGroupActions(
            2,
            false,
            [TimeGranularity.HOURLY, TimeGranularity.DAILY],
            TimeGranularity.HOURLY,
            QuickMathUtils.sum,
          ),
          ChartFullscreenActions.getActions(1, false),
          ChartDownloadGroupActionsUtils.DefaultChartDownloadOptions,
        ],
        chartMode: ChartMode,
        chartTheme: ChartTheme,
        chartType: ChartType.BAR,
        chartGranularity: TimeGranularity.HOURLY,
        currentStartDate: null,
        currentEndDate: null,
        defaultData: {},
        timelineDataChartOptions: null,
        isLoading: true,
        timelineData: null,
        timelineDataIntervalInMinutes: 1,
      };
    },
    computed: {
      localCacheKey() {
        return 'chartOptions.timelineDataChart';
      },
      isDeal() {
        return this.entity instanceof Package;
      },
      failuresFormatted() {
        return this.kpis == null || this.kpis.failures === 0 ? DASH : formatNumber(this.kpis.failures, '0,0');
      },
      noBidsFormatted() {
        return this.kpis == null || this.kpis.noBids === 0 ? DASH : formatNumber(this.kpis.noBids, '0,0');
      },
      requestsFormatted() {
        return this.kpis == null || this.kpis.requests === 0 ? DASH : formatNumber(this.kpis.requests, '0,0');
      },
      acceptedBidsFormatted() {
        return this.kpis == null || this.kpis.acceptedBids === 0 ? DASH : formatNumber(this.kpis.acceptedBids, '0,0');
      },
      failuresPercent() {
        return this.kpis == null || this.kpis.failures === 0 || this.kpis.requests === 0
          ? DASH
          : formatNumber(this.kpis.failures / this.kpis.requests, '0,0.00%');
      },
      noBidsPercent() {
        return this.kpis == null || this.kpis.noBids === 0 || this.kpis.requests === 0
          ? DASH
          : formatNumber(this.kpis.noBids / this.kpis.requests, '0,0.00%');
      },
      acceptedBidsPercent() {
        return this.kpis == null || this.kpis.acceptedBids === 0 || this.kpis.requests === 0
          ? DASH
          : formatNumber(this.kpis.acceptedBids / this.kpis.requests, '0,0.00%');
      },
      endDate() {
        return this.entity.endDate;
      },
      getRTBMetrics() {
        return this.isDeal ? this.getPackageDefaultRTBMetrics : this.getLineItemDefaultRTBMetrics;
      },
      showRTBMetricsGraph() {
        if (this.isLoading) {
          return false;
        }
        // HSSP-3201 - Always show graph for deals with bidding
        const isDealForBidding = this.isDeal && this.entity?.isForBidding;
        if (isDealForBidding) {
          return true;
        }
        return this.totalKpis?.requests > 0;
      },
      kpis() {
        if (this.filteredTimelineData == null) {
          return null;
        }
        return {
          failures: this.filteredTimelineData.failures.reduce((mem, x) => mem + x.value, 0),
          noBids: this.filteredTimelineData.noBids.reduce((mem, x) => mem + x.value, 0),
          requests: this.filteredTimelineData.requests.reduce((mem, x) => mem + x.value, 0),
          acceptedBids: this.filteredTimelineData.acceptedBids.reduce((mem, x) => mem + x.value, 0),
        };
      },
      totalKpis() {
        if (this.timelineData == null) {
          return null;
        }
        return {
          failures: this.timelineData.failures.reduce((mem, x) => mem + x.value, 0),
          noBids: this.timelineData.noBids.reduce((mem, x) => mem + x.value, 0),
          requests: this.timelineData.requests.reduce((mem, x) => mem + x.value, 0),
          acceptedBids: this.timelineData.acceptedBids.reduce((mem, x) => mem + x.value, 0),
        };
      },
      filteredTimelineData() {
        if (this.timelineData == null) return null;

        const start = this.currentStartDate;
        const end = this.currentEndDate;

        const dayGroupFn = (mem, x) => {
          const index = mem.findIndex((m) => m.time.toLocaleString() === x.time.toLocaleString());
          if (index > -1) {
            return [
              ...mem.filter((m) => m.time.toLocaleString() !== x.time.toLocaleString()),
              {
                time: mem[index].time,
                value: mem[index].value + x.value,
              },
            ];
          }
          return [...mem, x];
        };

        const filteredTimelineData = {
          failures: this.timelineData.failures.filter((x) => Interval.fromDateTimes(start, end).contains(x.time)),
          noBids: this.timelineData.noBids.filter((x) => Interval.fromDateTimes(start, end).contains(x.time)),
          requests: this.timelineData.requests.filter((x) => Interval.fromDateTimes(start, end).contains(x.time)),
          acceptedBids: this.timelineData.acceptedBids.filter((x) =>
            Interval.fromDateTimes(start, end).contains(x.time),
          ),
        };

        return this.timelineDataChartOptions != null && this.chartGranularity === TimeGranularity.DAILY
          ? {
              failures: filteredTimelineData.failures.reduce(dayGroupFn, []),
              noBids: filteredTimelineData.noBids.reduce(dayGroupFn, []),
              requests: filteredTimelineData.requests.reduce(dayGroupFn, []),
              acceptedBids: filteredTimelineData.acceptedBids.reduce(dayGroupFn, []),
            }
          : filteredTimelineData;
      },
    },
    methods: {
      ...mapActions('orgApp', [
        'getLineItemDefaultRTBMetrics',
        'getLineItemRTBMetricsForDay',
        'getPackageDefaultRTBMetrics',
      ]),
      capitalize(text) {
        return capitalize(text);
      },
      getTooltipDateFormatted(params, userTz) {
        const index = this.chartGranularity === TimeGranularity.DAILY ? params[0].dataIndex * 24 : params[0].dataIndex;
        const milliseconds = this.timelineDataChartOptions.xAxis.raw[index];
        const from = ChartTimezoneGroupActionsUtils.localizedDate(milliseconds, userTz);
        const to = ChartTimezoneGroupActionsUtils.localizedDate(milliseconds, userTz).plus(
          this.timelineDataIntervalInMinutes,
        );

        switch (this.chartGranularity) {
          case TimeGranularity.DAILY:
            return from.toLocaleString(DateTime.DATE_MED);
          default:
            return `${from.toLocaleString(DateTime.DATETIME_MED)} - ${to.toLocaleString(DateTime.DATETIME_MED)}`;
        }
      },
      refreshChartData() {
        if (this.filteredTimelineData == null) {
          this.timelineDataChartOptions = null;
          return;
        }
        this.timelineDataChartOptions = null;
        const series = [
          {
            cellType: CellType.INTEGER,
            name: capitalize(i18n.global.t('states.accepted')),
            type: this.chartType,
            stack: i18n.global.t('names.bid', { n: 2 }),
            data: this.filteredTimelineData.acceptedBids.map((x) => (x.value === 0 ? null : x.value)),
            smooth: true,
            areaStyle: {},
          },
          {
            cellType: CellType.INTEGER,
            name: capitalize(i18n.global.t('names.noBid', { n: 2 })),
            type: this.chartType,
            stack: i18n.global.t('names.bid', { n: 2 }),
            data: this.filteredTimelineData.noBids.map((x) => (x.value === 0 ? null : x.value)),
            smooth: true,
            areaStyle: {},
          },
          {
            cellType: CellType.INTEGER,
            name: capitalize(i18n.global.t('names.failure', { n: 2 })),
            type: this.chartType,
            stack: i18n.global.t('names.bid', { n: 2 }),
            data: this.filteredTimelineData.failures.map((x) => (x.value === 0 ? null : x.value)),
            smooth: true,
            areaStyle: {},
          },
        ];

        const { userTz } = ChartTimezoneGroupActionsUtils;
        const raw = this.filteredTimelineData.acceptedBids.map((x) => x.time.toMillis());
        const xAxisFormatter = (value, zone) =>
          ChartTimezoneGroupActionsUtils.localizedDate(value, zone).toLocaleString(
            this.chartGranularity === TimeGranularity.DAILY ? DateTime.DATE_MED : DateTime.DATETIME_MED,
          );

        this.timelineDataChartOptions = {
          calculable: true,
          xAxis: TimeseriesAxisX.fromObject({
            name: capitalize(i18n.global.t('names.date', { n: 1 })),
            raw,
            data: raw.map((r) => xAxisFormatter(r, userTz)),
            transform: xAxisFormatter,
            min: 0,
            granularity: TimeGranularity.HOURLY,
          }),
          yAxis: [
            {
              axisLabel: {
                show: true,
              },
              nameTextStyle: {
                fontWeight: 600,
              },
              name: capitalize(i18n.global.t('names.bid', { n: 2 })),
            },
          ],
          series,
          tooltip: {
            trigger: 'axis',
            formatter: (params) => {
              let html = `<b>${this.getTooltipDateFormatted(params, userTz)}</b><br><table>`;
              params
                .filter((x) => x.data != null)
                .forEach((x) => {
                  const value =
                    typeof x.data === 'object' ? formatNumber(x.data.value, '0,0') : formatNumber(x.data, '0,0');
                  html = `${html}<tr><td>${x.marker}</td><td>${capitalize(x.seriesName)}:</td><td style="text-align: right;">${value}</td></tr>`;
                });
              return `${html}</table>`;
            },
          },
          legend: {
            show: true,
            bottom: 0,
          },
          grid: {
            top: 85,
            left: 60,
            right: 16,
          },
        };
      },
      updateChartType(type) {
        this.chartType = type;
        this.refreshChartData();
      },
      updateChartGranularity(granularity) {
        this.chartGranularity = granularity;
      },
      getMetrics(start, end) {
        this.getRTBMetrics({
          [this.isDeal ? 'packageId' : 'lineItemId']: this.entity.id,
          start,
          end,
          keepSeries: true,
          showLoader: false,
        }).then(
          (response) => {
            this.isLoading = false;

            const data = response[Object.keys(response)[0]];

            const failures = data.failures_series.map((x) => {
              return {
                time: DateTime.fromSeconds(x[0]),
                value: Math.max(x[1], 0),
              };
            });
            const noBids = data.no_bids_series.map((x) => {
              return {
                time: DateTime.fromSeconds(x[0]),
                value: Math.max(x[1], 0),
              };
            });
            const requests = data.requests_series.map((x) => {
              return {
                time: DateTime.fromSeconds(x[0]),
                value: Math.max(x[1], 0),
              };
            });
            const overrides = data.override_series?.map((x) => x[1]) ?? [];
            const invalidOverrides = data.invalid_override_series?.map((x) => x[1]) ?? [];
            const acceptedBids = requests.map((x, i) => {
              return {
                time: x.time,
                value: Math.max(
                  (x?.value || 0) -
                    (noBids[i]?.value || 0) -
                    (failures[i]?.value || 0) -
                    ((overrides[x] || 0) - (invalidOverrides[x] || 0)),
                  0,
                ),
              };
            });

            const timelineData = {
              failures,
              noBids,
              requests,
              acceptedBids,
            };

            this.timelineDataIntervalInMinutes =
              requests.length > 1 ? requests[1].time.diff(requests[0].time, 'minutes') : 1;

            this.timelineData = timelineData;

            this.refreshChartData();

            const interval = setInterval(() => {
              if (this.$refs.timelineDataChart != null) {
                const settings = LocalCache.get(this.localCacheKey);
                if (settings != null) {
                  this.chartGranularity = settings.find((key) => key.id === ChartActionId.INTERVAL).optionSelected;
                  this.chartType = settings.find((key) => key.id === ChartActionId.TYPE)?.optionSelected;
                }
                this.$refs.timelineDataChart.updateChartInterval(
                  this.chartActions.find((a) => a.callback === 'intervalSelection'),
                  this.chartActions
                    .find((a) => a.callback === 'intervalSelection')
                    ?.options?.find((o) => o.id === this.chartGranularity),
                );

                clearInterval(interval);
              }
            }, 100);
          },
          () => {
            // Error handled in action.
          },
        );
      },
      getMetricsForCurrentDateRange() {
        //
        // Throttle calls
        //
        if (this.getMetricsForCurrentDateRangeTimer != null) {
          clearTimeout(this.getMetricsForCurrentDateRangeTimer);
        }
        this.getMetricsForCurrentDateRangeTimer = setTimeout(() => {
          if (this.filterStartDate != null && this.filterEndDate != null) {
            this.currentStartDate = this.filterStartDate;
            this.currentEndDate = this.filterEndDate;
            this.getMetrics(this.filterStartDate, this.filterEndDate);
          } else if (this.entity.hasEnded) {
            let start = DateTime.fromISO(this.endDate).minus({ days: 7 }).startOf('day');
            let end = DateTime.fromISO(this.endDate).endOf('day');

            //
            // First call is to find out what was the last time a request was sent.
            // We'll then do a second request 23 hours before that time.
            //
            this.getRTBMetrics({
              [this.isDeal ? 'packageId' : 'lineItemId']: this.entity.id,
              start,
              end,
              keepSeries: true,
            }).then((response) => {
              const data = response[Object.keys(response)[0]];

              const requests = data.requests_series.map((x) => {
                return {
                  time: DateTime.fromSeconds(x[0]),
                  value: x[1],
                };
              });

              if (requests.length === 0) {
                return;
              }

              end = DateTime.fromISO(requests[requests.length - 1].time).plus({ hours: 1 });
              start = DateTime.fromISO(end).minus({ hours: 23 });

              this.currentStartDate = start;
              this.currentEndDate = end;
              //
              // The second request.
              //
              this.getMetrics(start, end);
            });
          } else {
            const start = DateTime.local().minus({ days: 1 }).startOf('day');
            const end = DateTime.local().endOf('day');

            this.currentStartDate = start;
            this.currentEndDate = end;

            this.getMetrics(start, end);
          }
        }, 200);
      },
    },
    watch: {
      filterStartDate() {
        this.getMetricsForCurrentDateRange();
      },
      filterEndDate() {
        this.getMetricsForCurrentDateRange();
      },
    },
    mounted() {
      this.isLoading = true;
      this.getMetricsForCurrentDateRange();
    },
  };
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;

  #app {
    .echarts {
      width: 100%;
    }

    .chart-container {
      min-height: 27.2rem;
      margin-top: 3rem;
    }

    .time-ranges-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding-bottom: 1.6rem;

      .btn-group {
        .btn {
          font-size: 1.1rem;
        }
      }

      .badge {
        display: flex;
        align-items: center;
        padding: 0.6rem 1.2rem;

        svg {
          stroke: var(--textMutedColor);

          width: 1.6rem;
          height: 1.6rem;
          stroke-width: 0.2rem;
          margin-right: 0.6rem;
        }
      }
    }

    .kpis {
      display: flex;
      justify-content: space-between;
      margin: 0 3.2rem;

      .kpi {
        margin-top: 1.6rem;
        display: flex;
        flex-direction: row;
        align-items: center;

        svg {
          width: 4rem;
          height: 4rem;
          margin-right: 0.3rem;
          position: relative;
          top: 0.2rem;
        }

        .value-and-label {
          margin-left: 1rem;
          display: flex;
          flex-direction: column;

          .value {
            font-size: 2.3rem;
            font-weight: 600;

            .percent {
              font-size: 60%;
              font-weight: 400;
              margin-left: 1rem;
            }
          }

          .label {
            color: var(--textMutedColor);

            text-transform: uppercase;
            font-size: 1.3rem;
            font-weight: 600;
          }
        }
      }
    }
  }
</style>
